import {
  FlowNodeBaseType,
  WorkflowNodeLinesData,
  type FlowNodeEntity,
} from '@flowgram.ai/free-layout-editor';
import { TVFlowNodeNextTypeEnum } from '../config/node.config';

/**
 * 获取节点所在的群组父节点。
 */
function getParentGroupNode(node: FlowNodeEntity): FlowNodeEntity | undefined {
  const { parent } = node;
  if (!parent || parent.flowNodeType !== FlowNodeBaseType.GROUP) {
    return undefined;
  }
  return parent;
}

type StoryboardChildTool = {
  sourceScriptNodeId?: string;
  sourceStoryboardGroupId?: string;
};

type StoryboardGroupData = {
  groupMode?: string;
  nodeOrder?: string[];
  sourceScriptNodeId?: string;
  sourceStoryboardGroupId?: string;
};

/**
 * 读取分镜组节点 data。
 */
function getGroupData(groupNode: FlowNodeEntity): StoryboardGroupData {
  return (groupNode.toJSON?.()?.data ?? {}) as StoryboardGroupData;
}

/**
 * 按 nodeOrder 获取分镜组内有序子节点。
 */
function getOrderedChildNodes(groupNode: FlowNodeEntity): FlowNodeEntity[] {
  const nodeOrder = getGroupData(groupNode).nodeOrder ?? [];
  if (!nodeOrder.length) {
    return groupNode.blocks ?? [];
  }

  const nodeMap = new Map((groupNode.blocks ?? []).map((node) => [node.id, node]));
  const orderedNodes = nodeOrder
    .map((nodeId) => nodeMap.get(nodeId))
    .filter((node): node is FlowNodeEntity => Boolean(node));
  const orderedIdSet = new Set(orderedNodes.map((node) => node.id));
  return [
    ...orderedNodes,
    ...(groupNode.blocks ?? []).filter((node) => !orderedIdSet.has(node.id)),
  ];
}

/**
 * 从子节点 tool 字段读取来源脚本节点 id。
 */
function getSourceScriptNodeIdFromChildren(groupNode: FlowNodeEntity): string | undefined {
  return getOrderedChildNodes(groupNode)
    .map((childNode) => (childNode.toJSON?.()?.data as { tool?: StoryboardChildTool; })?.tool?.sourceScriptNodeId)
    .find((nodeId): nodeId is string => Boolean(nodeId));
}

/**
 * 从输入连线查找关联的脚本节点。
 */
function getInputScriptNode(groupNode: FlowNodeEntity): FlowNodeEntity | undefined {
  const linesData = groupNode.getData(WorkflowNodeLinesData);
  return (linesData?.inputNodes ?? [])
    .find((node) => node.flowNodeType === TVFlowNodeNextTypeEnum.SCRIPT_NODE);
}

/**
 * 判断是否为脚本生成的图片分镜组。
 */
export function isScriptStoryboardImageGroup(node: FlowNodeEntity): boolean {
  if (node.flowNodeType !== FlowNodeBaseType.GROUP) return false;
  const groupData = getGroupData(node);
  if (groupData.groupMode !== 'storyboard') return false;
  const childNodes = getOrderedChildNodes(node);
  if (!childNodes.length) return false;
  const allImages = childNodes.every(
    (childNode) => childNode.flowNodeType === TVFlowNodeNextTypeEnum.IMAGE_NODE,
  );
  if (!allImages) return false;
  return Boolean(groupData.sourceScriptNodeId || getSourceScriptNodeIdFromChildren(node) || getInputScriptNode(node));
}

/**
 * 判断是否为脚本分镜图片批量生成的视频分镜组。
 */
export function isScriptPipelineVideoStoryboardGroup(node: FlowNodeEntity): boolean {
  if (node.flowNodeType !== FlowNodeBaseType.GROUP) return false;
  const groupData = getGroupData(node);
  if (groupData.groupMode !== 'storyboard') return false;
  const childNodes = getOrderedChildNodes(node);
  if (!childNodes.length) return false;
  const allVideos = childNodes.every(
    (childNode) => childNode.flowNodeType === TVFlowNodeNextTypeEnum.VIDEO_NODE,
  );
  if (!allVideos) return false;
  const hasSourceStoryboardGroup = Boolean(groupData.sourceStoryboardGroupId) || childNodes.some((childNode) => {
    const tool = (childNode.toJSON?.()?.data as { tool?: StoryboardChildTool; })?.tool;
    return Boolean(tool?.sourceStoryboardGroupId);
  });
  return hasSourceStoryboardGroup
    || Boolean(groupData.sourceScriptNodeId || getSourceScriptNodeIdFromChildren(node) || getInputScriptNode(node));
}

/**
 * 判断是否为需锁定子节点交互的脚本来源分镜组（图片分镜组或脚本管线视频分镜组）。
 */
export function isLockedScriptStoryboardGroup(node: FlowNodeEntity): boolean {
  return isScriptStoryboardImageGroup(node) || isScriptPipelineVideoStoryboardGroup(node);
}

/**
 * 判断节点是否位于锁定脚本分镜组内部。
 */
export function isNodeInsideLockedScriptStoryboardGroup(node: FlowNodeEntity): boolean {
  const parentGroup = getParentGroupNode(node);
  if (!parentGroup) {
    return false;
  }
  return isLockedScriptStoryboardGroup(parentGroup);
}

/**
 * 判断节点是否受锁定脚本分镜组子节点约束（组内子节点不可拖拽、不可单独删除）。
 */
export function isUnderLockedScriptStoryboardChildConstraint(node: FlowNodeEntity): boolean {
  return isNodeInsideLockedScriptStoryboardGroup(node);
}

/**
 * 选区中是否包含锁定脚本分镜组（作为群组节点选中）。
 */
export function hasLockedScriptStoryboardGroupInSelection(nodes: FlowNodeEntity[]): boolean {
  return nodes.some(
    (node) => node.flowNodeType === FlowNodeBaseType.GROUP && isLockedScriptStoryboardGroup(node),
  );
}

/**
 * 从节点列表中排除锁定脚本分镜组内的子节点。
 */
export function excludeLockedScriptStoryboardChildren(nodes: FlowNodeEntity[]): FlowNodeEntity[] {
  return nodes.filter((node) => !isUnderLockedScriptStoryboardChildConstraint(node));
}
